import useSWR from 'swr';
import dramabox from '@lib/dramabox.js';

const fetcher = async (key, ...args) => {
  const [method] = key.split(':');

  if (typeof dramabox[method] === 'function') {
    return await dramabox[method](...args);
  }

  throw new Error(`Method ${method} not found in dramabox`);
};

// Hook for recommended books
export const useRecommendedBooks = (pageNo = 1) => {
  const { data, error, isLoading, mutate } = useSWR(
    ['getRecommendedBooks', pageNo],
    () => dramabox.getRecommendedBooks(pageNo),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
      keepPreviousData: true
    }
  );

  return {
    books: data || [],
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for drama detail
export const useDramaDetail = (bookId) => {
  const { data, error, isLoading, mutate } = useSWR(
    bookId ? ['getDramaDetailV2', bookId] : null,
    () => dramabox.getDramaDetailV2(bookId),
    {
      revalidateOnFocus: false,
      dedupingInterval: 300000, // 5 minutes
    }
  );
  return {
    drama: data?.drama || null,
    chapters: data?.chapters || [],
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for chapters with video URLs
export const useChapters = (bookId, quality, eps) => {
  const { data, error, isLoading, mutate } = useSWR(
    bookId ? ['getChapters', bookId, quality, eps] : null,
    () => dramabox.getChapters(bookId, quality, eps),
    {
      revalidateOnFocus: false,
      dedupingInterval: 0, // Always refetch on parameter change
    }
  );

  return {
    videos: data || [],
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for batch download (get video paths)
export const useBatchDownload = (bookId, chapterIdList) => {
  const { data, error, isLoading, mutate } = useSWR(
    bookId && chapterIdList?.length > 0
      ? ['batchDownload', bookId, chapterIdList]
      : null,
    () => dramabox.batchDownload(bookId, chapterIdList),
    {
      revalidateOnFocus: false,
      dedupingInterval: 600000, // 10 minutes
    }
  );

  return {
    videos: data || [],
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for drama list
export const useDramaList = (pageNo = 1, pageSize = 20) => {
  const { data, error, isLoading, mutate } = useSWR(
    ['getDramaList', pageNo, pageSize],
    () => dramabox.getDramaList(pageNo, pageSize),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false, // ✅ Don't refetch on reconnect
      dedupingInterval: 120000, // ✅ Increased to 2 minutes
      keepPreviousData: true
    }
  );

  return {
    books: data?.book || [],
    isMore: data?.isMore || false,
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for search
export const useSearchDrama = (keyword, pageNo = 1, pageSize = 20) => {
  const { data, error, isLoading, mutate } = useSWR(
    keyword ? ['searchDrama', keyword, pageNo, pageSize] : null,
    () => dramabox.searchDrama(keyword, pageNo, pageSize),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
      keepPreviousData: true
    }
  );

  return {
    books: data?.book || [],
    isMore: data?.isMore || false,
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for trending dramas
export const useTrendingDramas = () => {
  const { data, error, isLoading, mutate } = useSWR(
    'searchDramaIndex',
    () => dramabox.getDramaList(1, 24), // ✅ Reduced from 54 to 24
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false, // ✅ Don't refetch on reconnect
      dedupingInterval: 600000, // ✅ Increased to 10 minutes
      keepPreviousData: true // ✅ Keep previous data while loading
    }
  );

  return {
    dramas: data || [],
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for filter list
export const useFilterList = (pageNo = 1, pageSize = 30) => {
  const { data, error, isLoading, mutate } = useSWR(
    ['getFilterList', pageNo, pageSize],
    () => dramabox.getFilterList(pageNo, pageSize),
    {
      revalidateOnFocus: false,
      dedupingInterval: 600000, // 10 minutes - filters don't change often
    }
  );

  return {
    filters: data || { region: [], sound: [], vip: [], tag1: [], tag2: [], sort: [] },
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for books from filter
export const useBookFromFilter = (pageNo = 1, pageSize = 20, filters = {}) => {
  const { data, error, isLoading, mutate } = useSWR(
    ['getBookFromFilter', pageNo, pageSize, JSON.stringify(filters)],
    () => dramabox.getBookFromFilter(pageNo, pageSize, filters),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
      keepPreviousData: true
    }
  );

  return {
    books: data?.book || [],
    isMore: data?.isMore || false,
    isLoading,
    isError: error,
    mutate
  };
};

// Hook for category books
export const useCategorie = (tagId = '1452', pageNo = 1, pageSize = 20, sourceBookId = '41000118948', sortType = 2) => {
  const { data, error, isLoading, mutate } = useSWR(
    ['getCategorie', tagId, pageNo, pageSize, sourceBookId, sortType],
    () => dramabox.getCategorie(tagId, pageNo, pageSize, sourceBookId, sortType),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
      keepPreviousData: true
    }
  );

  return {
    books: data || [],
    isLoading,
    isError: error,
    mutate
  };
};
